import { Grid, Card, CardContent, Typography } from "@mui/material";
import { useAlarmDataGrid } from "./hooks";
import React, { useEffect } from "react";
import TopOffender from "./TopOffender/TopOffender";
import DisconnectedDevice from "./DisconnectedDevice";
import DisconnectedRadioNode from "./DisconnectedRadioNode";
import AlarmDisplay from "../../Nodes/AlarmDisplay/AlarmDisplay";
import EventDisplay from "../../Nodes/EventDisplay/EventDisplay";
import { useSelector } from "react-redux";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { useStoreDispatch } from "../../../store/hooks";
import { resetEventFilters as resetAlarmFilters } from "../../../store/filters/alarms/reducers";
import { resetEventFilters } from "../../../store/filters/events/EventFilterReducer";
import { resetKeywordForScope } from "../../../store/search/reducer";
import { setPaginationModel } from "../../../store/helpers/helperReducers";
import { initialState } from "../../../store/helpers/helperInitialState";
import CellsTable from "./CellsDatagrid/CellsTable";

type DashboardSection = {
  id?: string;
  title: string;
  component: React.ReactNode;
  grid: { xs: number; md?: number };
  sx: { mt?: number; pr?: { md: number }; pl?: { md: number } };
};

const AlarmDataGrid: React.FC<{ alarmsGridId: string }> = ({ alarmsGridId }) => {

  const dispatch = useStoreDispatch();

  useEffect(() => {
    dispatch(resetAlarmFilters());
    dispatch(resetEventFilters());
    dispatch(resetKeywordForScope({ scope: "alarms" }));
    dispatch(resetKeywordForScope({ scope: "events" }));
    dispatch(setPaginationModel({ alarmDisplay: initialState.pagination }));
    dispatch(setPaginationModel({ eventDisplay: initialState.pagination }));
  }, [dispatch]);

  const { paperSx, titleSx } = useAlarmDataGrid()
  const selectedGeoRegions = useSelector(selectedGeoRegionIdsSelector) || [];

  const dashboardSections: DashboardSection[] = [
    {
      title: "Disconnected Devices",
      component: <DisconnectedDevice selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12, md: 6 },
      sx: { pr: { md: 2.5 } },
    },
    {
      title: "Disconnected Radio Nodes",
      component: <DisconnectedRadioNode selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12, md: 6 },
      sx: { pl: { md: 2.5 } },
    },
    {
      title: "Non Operational Cells",
      component: <CellsTable selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12 },
      sx: { mt: 4 },
    },
    {
      title: "Top Offenders",
      component: <TopOffender selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12 },
      sx: { mt: 4 },
    },
    {
      id: alarmsGridId,
      title: "Alarms",
      component: <AlarmDisplay selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12 },
      sx: { mt: 4 },
    },
    {
      title: "Events",
      component: <EventDisplay selectedGeoRegions={selectedGeoRegions} />,
      grid: { xs: 12 },
      sx: { mt: 4 },
    },
  ];

  return (
    <Grid container spacing={3} style={{ backgroundColor: "var(--background)" }}>
      {dashboardSections.map(({ id, title, component, grid, sx }) => (
        <Grid
          key={title}
          item
          {...grid}
          sx={id ? { ...sx, scrollMarginTop: "84px" } : sx}
          id={id}
        >
          <Card
            variant="outlined"
            sx={{
              backgroundColor: "var(--card)",
              color: "var(--card-foreground)",
              borderColor: "var(--border)",
              borderRadius: "calc(var(--radius) + 4px)",
              boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                padding: "var(--spacing-6, 24px)",
                "&:last-child": { paddingBottom: "var(--spacing-6, 24px)" },
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "var(--font-weight-semibold, 600)", color: "var(--card-foreground)", letterSpacing: "-0.025em" }}
                >
                  {title}
                </Typography>
              </div>
              <div style={{ marginTop: 4, flex: 1 }}>
                {component}
              </div>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default AlarmDataGrid;
